import React, { useRef, useState } from "react";
import { RefreshCw, Wand2, AlertCircle, Trash2, Plus } from "lucide-react";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";

/**
 * DiagnosisReview — review the diagnosis: edit each gap's description, remove
 * gaps, or add your own free-text gaps, then generate.
 *
 * The metric name and fail/weak verdict are LLM-generated context shown
 * read-only; only the description is editable. Added gaps are plain text.
 *
 * Props:
 *   diagnosis    — { summary, gaps: [{ id, metric, verdict, why, evidence_calls }] }
 *   onRegenerate — () => void
 *   onGenerate   — (selectedIds: string[], editedGaps: object[]) => void
 */
export default function DiagnosisReview({ diagnosis, onRegenerate, onGenerate }) {
  const { summary = "", gaps = [] } = diagnosis || {};

  const [localGaps, setLocalGaps] = useState(() =>
    gaps.map((g) => ({ ...g, evidence_calls: g.evidence_calls || [] }))
  );
  const [selected, setSelected] = useState(() => new Set(gaps.map((g) => g.id)));
  const newIdRef = useRef(0);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const updateWhy = (id, value) =>
    setLocalGaps((prev) => prev.map((g) => (g.id === id ? { ...g, why: value } : g)));

  const removeGap = (id) => {
    setLocalGaps((prev) => prev.filter((g) => g.id !== id));
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const addGap = () => {
    const id = `custom-${newIdRef.current++}`;
    // Plain additional gap — no metric/verdict needed.
    setLocalGaps((prev) => [...prev, { id, metric: "", verdict: "", why: "", evidence_calls: [] }]);
    setSelected((prev) => new Set(prev).add(id));
  };

  const handleGenerate = () => onGenerate([...selected], localGaps);

  const verdictVariant = (v) =>
    (v || "").toLowerCase() === "fail" ? "destructive" : "warning";

  const selectedCount = localGaps.filter((g) => selected.has(g.id)).length;

  return (
    <div className="flex flex-col gap-6">
      {summary && (
        <div className="bg-muted/50 border border-border rounded-lg p-4 flex gap-3">
          <AlertCircle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
          <p className="text-sm text-foreground leading-relaxed">{summary}</p>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Gaps ({localGaps.length})
          </p>
          <button
            type="button"
            onClick={addGap}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            <Plus className="w-3.5 h-3.5" /> Add gap
          </button>
        </div>

        {localGaps.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-border rounded-lg">
            <p className="text-sm text-muted-foreground">
              No gaps. Add one to steer the rewrite, or re-analyze.
            </p>
          </div>
        ) : (
          localGaps.map((gap) => {
            const isChecked = selected.has(gap.id);
            const hasMeta = !!gap.metric;
            return (
              <div
                key={gap.id}
                className={cn(
                  "flex items-start gap-3 p-4 border rounded-lg transition-colors",
                  isChecked ? "border-primary bg-primary/5" : "border-border"
                )}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  aria-label={gap.metric || "additional gap"}
                  className="mt-1 shrink-0 accent-primary"
                  onChange={() => toggle(gap.id)}
                />
                <div className="flex flex-col gap-2 min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {hasMeta ? (
                      <>
                        {gap.verdict && (
                          <Badge variant={verdictVariant(gap.verdict)} className="capitalize">
                            {gap.verdict}
                          </Badge>
                        )}
                        <span className="text-sm font-medium text-foreground">{gap.metric}</span>
                      </>
                    ) : (
                      <span className="text-sm font-medium text-muted-foreground">Additional gap</span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeGap(gap.id)}
                      aria-label="Remove gap"
                      className="ml-auto text-muted-foreground hover:text-destructive transition-colors shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <textarea
                    value={gap.why || ""}
                    onChange={(e) => updateWhy(gap.id, e.target.value)}
                    placeholder={
                      hasMeta
                        ? "What the instruction is failing to cater to, and how to fix it…"
                        : "Describe an additional gap to address in the rewrite…"
                    }
                    rows={hasMeta ? 3 : 2}
                    className="w-full text-sm border border-input rounded-md px-2.5 py-2 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                  />
                  {gap.evidence_calls && gap.evidence_calls.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {gap.evidence_calls.map((call, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
                        >
                          {call.label || `#${call.id}`}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-border">
        <button
          type="button"
          onClick={onRegenerate}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-md hover:text-foreground hover:border-input transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Re-analyze
        </button>

        <button
          type="button"
          onClick={handleGenerate}
          disabled={selectedCount === 0}
          className={cn(
            "flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md transition-colors",
            selectedCount === 0 ? "opacity-50 cursor-not-allowed" : "hover:bg-primary/90"
          )}
        >
          <Wand2 className="w-4 h-4" />
          Generate rewrite
        </button>
      </div>
    </div>
  );
}
